import { FC, useEffect, useMemo, useState } from "react";

import { StatsIcon } from "../../../assets/img/icons";
import ConnectedLayout from "../../../components/ConnectedLayout";
import Header from "../../../components/Header";
import CalendarStats from "./CalendarStats";

import { useMediaQuery } from "@mui/material";
import Divider from "../../../components/Divider";
import {
    CountersStatsResponse,
    getDashboardStats,
} from "../../../services/api/admin/stats";
import {
    getFirstDayOfSpecificMonth,
    getFormattedDateInput,
    getLastDayOfSpecificMonth,
} from "../../../utils/date";
import HeaderStats from "./HeaderStats";
import StatsFilters from "./StatsFilters";
import styles from "./styles.module.scss";
import { INITIAL_STATS_FILTERS_STATE, StatsFiltersState, getDefaultStatsDateRange } from "./types";

const EMPTY_COUNTERS: CountersStatsResponse = {
    totalTrades: 0,
    totalPayouts: 0,
    activeMembers: 0,
    trainedMembersCount: 0,
    averageDayWinPercentage: 0,
    dayTradingUsersCount: 0,
    scalpingUsersCount: 0,
};

const Stats: FC = () => {
    const isMobile = useMediaQuery("(max-width: 1050px)");

    const now = useMemo(() => new Date(), []);
    const defaultStatsDateRange = useMemo(
        () => getDefaultStatsDateRange(now),
        [now],
    );
    const [month, setMonth] = useState<Date>(
        new Date(now.getFullYear(), now.getMonth(), 1),
    );

    const [filters, setFilters] = useState<StatsFiltersState>(
        INITIAL_STATS_FILTERS_STATE,
    );
    const [counters, setCounters] =
        useState<CountersStatsResponse>(EMPTY_COUNTERS);
    const [dailyStats, setDailyStats] = useState<Record<string, number>>({});
    const [isLoading, setIsLoading] = useState(true);

    const updateFilters = (updates: Partial<StatsFiltersState>) => {
        setFilters((prev) => ({
            ...prev,
            ...updates,
            hasFiltersBeenModified: true,
        }));
    };

    const handleResetFilters = () => {
        setFilters(INITIAL_STATS_FILTERS_STATE);
        setMonth(new Date(now.getFullYear(), now.getMonth(), 1));
    };

    const handleEndDateChange = (date: Date) => {
        updateFilters({ endDate: date });
        setMonth(new Date(date.getFullYear(), date.getMonth(), 1));
    };

    // Par défaut : les 90 derniers jours (aligné sur les métriques apprentissage)
    const effectiveStartDate = useMemo(
        () =>
            filters.hasFiltersBeenModified && filters.startDate
                ? filters.startDate
                : defaultStatsDateRange.startDate,
        [
            filters.hasFiltersBeenModified,
            filters.startDate,
            defaultStatsDateRange.startDate,
        ],
    );
    const effectiveEndDate = useMemo(
        () =>
            filters.hasFiltersBeenModified && filters.endDate
                ? filters.endDate
                : defaultStatsDateRange.endDate,
        [
            filters.hasFiltersBeenModified,
            filters.endDate,
            defaultStatsDateRange.endDate,
        ],
    );

    const pickerStartDate = filters.startDate ?? defaultStatsDateRange.startDate;
    const pickerEndDate = filters.endDate ?? defaultStatsDateRange.endDate;

    const displayedMonthStart = useMemo(
        () =>
            getFirstDayOfSpecificMonth(
                month.getFullYear(),
                month.getMonth(),
            ),
        [month],
    );
    const displayedMonthEnd = useMemo(
        () => getLastDayOfSpecificMonth(month.getFullYear(), month.getMonth()),
        [month],
    );
    const calendarStartDate =
        effectiveStartDate < displayedMonthStart
            ? effectiveStartDate
            : displayedMonthStart;
    const calendarEndDate =
        effectiveEndDate > displayedMonthEnd
            ? effectiveEndDate
            : displayedMonthEnd;

    useEffect(() => {
        let isActive = true;
        setIsLoading(true);

        getDashboardStats(
            getFormattedDateInput(effectiveStartDate),
            getFormattedDateInput(effectiveEndDate),
            getFormattedDateInput(calendarStartDate),
            getFormattedDateInput(calendarEndDate),
            {
                timeslotFilter: filters.timeslotFilter,
                challengeFilter: filters.challengeFilter,
                tags: filters.selectedTags,
                roles: filters.selectedRoles,
                favoritesOnly: filters.favoritesOnly,
            },
        )
            .then((response) => {
                if (!isActive || !response) return;
                setCounters(response.counters);
                setDailyStats(
                    Object.fromEntries(
                        response.calendar.map((item) => [
                            item.date,
                            item.totalTrades,
                        ]),
                    ),
                );
            })
            .catch(() => {
                if (!isActive) return;
                setCounters(EMPTY_COUNTERS);
                setDailyStats({});
            })
            .finally(() => {
                if (isActive) setIsLoading(false);
            });

        return () => {
            isActive = false;
        };
    }, [
        effectiveStartDate,
        effectiveEndDate,
        calendarStartDate,
        calendarEndDate,
        filters.timeslotFilter,
        filters.challengeFilter,
        filters.selectedTags,
        filters.selectedRoles,
        filters.favoritesOnly,
    ]);

    return (
        <ConnectedLayout>
            <Header icon={StatsIcon} title="Les statistiques" />
            <div className={styles.content}>
                <StatsFilters
                    filters={filters}
                    pickerStartDate={pickerStartDate}
                    pickerEndDate={pickerEndDate}
                    onUpdateFilters={updateFilters}
                    onEndDateChange={handleEndDateChange}
                    onResetFilters={handleResetFilters}
                />
                {isMobile && <Divider className={styles.divider} />}
                <HeaderStats
                    counters={counters}
                    isLoading={isLoading}
                />
                <CalendarStats
                    month={month}
                    setMonth={setMonth}
                    startDate={effectiveStartDate}
                    endDate={effectiveEndDate}
                    dailyStats={dailyStats}
                />
            </div>
        </ConnectedLayout>
    );
};

export default Stats;
